import { format as d3Format } from 'd3';
import getContrastColor from '../structureData/getContrastColor.js';

function getValueKey(context) {
    return context.chart.options?.indexAxis === 'y' ? 'x' : 'y';
}

function getCategoryKey(context) {
    return context.chart.options?.indexAxis === 'y' ? 'y' : 'x';
}

function toNumber(value) {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
}

function getPoint(context) {
    return context.dataset.data[context.dataIndex];
}

function getRenderedValue(point, context) {
    return toNumber(point?.[getValueKey(context)]);
}

function getRawValue(point, context) {
    return point?._rawY !== undefined
        ? toNumber(point._rawY)
        : getRenderedValue(point, context);
}

function getCategory(point, context) {
    return point?.[getCategoryKey(context)];
}

function isDatasetVisible(chart, datasetIndex) {
    return typeof chart.isDatasetVisible === 'function'
        ? chart.isDatasetVisible(datasetIndex)
        : true;
}

function findPointForCategory(dataset, category, context) {
    return dataset.data.find(
        (point) => getCategory(point, context) === category
    );
}

// When dynamicCategoryLegendOnClick hides a dataset it empties dataset.data and
// stores the original points in dataset._backup_. Use the backup when available so
// hidden groups still contribute to the category total.
function getDataForTotal(dataset) {
    return dataset._backup_ ?? dataset.data ?? [];
}

function getRawTotal(context) {
    const point = getPoint(context);
    const category = getCategory(point, context);

    return context.chart.data.datasets.reduce((total, dataset) => {
        const match = getDataForTotal(dataset).find(
            (p) => getCategory(p, context) === category
        );
        return total + getRawValue(match, context);
    }, 0);
}

// Sum only datasets that are currently visible (not hidden by legend toggle or
// dynamicCategoryAxis). Used for total/inside annotation labels so the displayed
// total reflects what the user sees.
function getVisibleRawTotal(context) {
    const point = getPoint(context);
    const category = getCategory(point, context);

    return context.chart.data.datasets.reduce((total, dataset, i) => {
        if (dataset._backup_) return total;
        if (!isDatasetVisible(context.chart, i)) return total;
        const match = dataset.data.find(
            (p) => getCategory(p, context) === category
        );
        return total + getRawValue(match, context);
    }, 0);
}

function getSpec(context, spec) {
    return context.chart.data?._spec_ || spec;
}

function isPercentStat(context, spec) {
    const s = getSpec(context, spec);
    return s?.stat === 'percent' || s?.position === 'fill';
}

function getPercentValue(point, context, spec) {
    const rendered = getRenderedValue(point, context);
    if (isPercentStat(context, spec)) return rendered;

    const total = getRawTotal(context);
    return total === 0 ? 0 : (getRawValue(point, context) / total) * 100;
}

function resolveLabelValue(point, context, options, mode, spec) {
    const configuredValue = options.value ?? 'auto';
    const valueType =
        configuredValue === 'auto'
            ? isPercentStat(context, spec)
                ? 'percent'
                : 'raw'
            : configuredValue;

    if (mode === 'total') {
        return { value: getVisibleRawTotal(context), valueType: 'raw' };
    }

    if (valueType === 'percent') {
        return {
            value: getPercentValue(point, context, spec),
            valueType,
        };
    }

    if (valueType === 'value') {
        return {
            value: getRenderedValue(point, context),
            valueType,
        };
    }

    return {
        value: getRawValue(point, context),
        valueType: 'raw',
    };
}

function defaultFormat(value, valueType) {
    const formatter =
        valueType === 'percent' ? d3Format('.1f') : d3Format('~g');
    const suffix = valueType === 'percent' ? '%' : '';
    return `${formatter(value)}${suffix}`;
}

function interpolateTemplate(template, details) {
    const knownTokens = {
        fill: String(details.fill ?? ''),
        value: d3Format('~g')(details.value),
        percent: `${d3Format('.1f')(details.percent)}%`,
        category: String(details.category ?? ''),
    };
    return template.replace(/\{(\w+)\}/g, (match, key) =>
        key in knownTokens ? knownTokens[key] : match
    );
}

function buildDetails(point, context, mode, value, valueType) {
    const rawValue = getRawValue(point, context);
    const rawTotal = getRawTotal(context);
    const labelValue = mode === 'total' ? value : rawValue;
    const percent = rawTotal === 0 ? 0 : (labelValue / rawTotal) * 100;

    return {
        mode,
        valueType,
        point,
        total: rawTotal,
        fill: point?._fill,
        value: labelValue,
        percent,
        category: getCategory(point, context),
        datum: point?._datum,
    };
}

function formatLabel(point, context, options, mode, spec) {
    const { value, valueType } = resolveLabelValue(
        point,
        context,
        options,
        mode,
        spec
    );

    if (options.formatter !== undefined) {
        const details = buildDetails(point, context, mode, value, valueType);

        if (typeof options.formatter === 'string') {
            return interpolateTemplate(options.formatter, details);
        }

        return options.formatter(value, context, details);
    }

    if (options.format) {
        const usesPercentFormat = options.format.includes('%');
        const formatValue =
            valueType === 'percent' && usesPercentFormat ? value / 100 : value;
        const suffix = valueType === 'percent' && !usesPercentFormat ? '%' : '';
        return `${d3Format(options.format)(formatValue)}${suffix}`;
    }

    return defaultFormat(value, valueType);
}

function hasVisibleValueForCategory(context, datasetIndex, category) {
    if (!isDatasetVisible(context.chart, datasetIndex)) return false;
    const dataset = context.chart.data.datasets[datasetIndex];
    const point = findPointForCategory(dataset, category, context);
    return Math.abs(getRawValue(point, context)) > 0;
}

function isLastVisibleDatasetForCategory(context) {
    const point = getPoint(context);
    const category = getCategory(point, context);

    for (
        let i = context.chart.data.datasets.length - 1;
        i >= context.datasetIndex;
        i--
    ) {
        if (hasVisibleValueForCategory(context, i, category)) {
            return i === context.datasetIndex;
        }
    }

    return false;
}

function getElement(context) {
    return context.chart.getDatasetMeta?.(context.datasetIndex)?.data?.[
        context.dataIndex
    ];
}

function isLargeEnoughForValueAxis(context, options) {
    const minSize = options.minSize ?? 0;
    if (!minSize) return true;

    const element = getElement(context);
    if (!element) return true;

    const size =
        context.chart.options?.indexAxis === 'y'
            ? element.width
            : element.height;

    return size === undefined || size >= minSize;
}

// The bar's extent along the category axis (its thickness), the opposite
// dimension from the one measured by `minSize`.
function getCategoryThickness(context, element) {
    return context.chart.options?.indexAxis === 'y'
        ? element.height
        : element.width;
}

// Resolves `options.font` (a CSS font string or a Chart.js-style font object
// with size/family/weight/style) into a canvas `ctx.font` string, mirroring
// how chartjs-plugin-datalabels/Chart.js resolve fonts. Returns undefined
// when there's nothing to resolve, in which case the canvas's current font
// is used as a best-effort approximation.
function resolveFontString(font) {
    if (!font) return undefined;
    if (typeof font === 'string') return font;
    if (typeof font === 'function') return undefined;

    const size = font.size ?? 12;
    const family =
        font.family ?? "'Helvetica Neue', Helvetica, Arial, sans-serif";
    const style = font.style ? `${font.style} ` : '';
    const weight = font.weight ? `${font.weight} ` : '';

    return `${style}${weight}${size}px ${family}`;
}

// Resolves and caches the label text for a given render context so that the
// same text isn't computed twice (once by the category-overlap check, once
// by the datalabels plugin's own `formatter`) for the same label render.
function resolveLabelText(context, options, mode, spec) {
    const cacheKey = `_${mode}LabelText`;
    if (Object.prototype.hasOwnProperty.call(context, cacheKey)) {
        return context[cacheKey];
    }

    const point = getPoint(context);
    const text = formatLabel(point, context, options, mode, spec);
    context[cacheKey] = text;
    return text;
}

// Guards against a label being wider than the bar itself along the category
// axis (see #547). Measures the resolved label text with the canvas so no
// per-chart tuning is required. Opt out via `options.avoidCategoryOverlap =
// false`. Falls back to "fits" (no-op) whenever the heuristic can't be
// evaluated, so it never hides a label it isn't confident about.
function fitsCategoryAxis(context, options, mode, spec) {
    if (options.avoidCategoryOverlap === false) return true;

    const ctx = context.chart.ctx;
    if (!ctx || typeof ctx.measureText !== 'function') return true;

    const element = getElement(context);
    if (!element) return true;

    const thickness = getCategoryThickness(context, element);
    if (thickness === undefined) return true;

    const text = resolveLabelText(context, options, mode, spec);
    if (text === undefined || text === null) return true;

    const font = resolveFontString(options.font);
    const previousFont = font !== undefined ? ctx.font : undefined;
    if (font !== undefined) {
        ctx.font = font;
    }

    const { width: textWidth } = ctx.measureText(String(text));

    if (font !== undefined) {
        ctx.font = previousFont;
    }

    return textWidth <= thickness;
}

function isLargeEnoughForSegment(context, options, spec) {
    return (
        isLargeEnoughForValueAxis(context, options) &&
        fitsCategoryAxis(context, options, 'segment', spec)
    );
}

function isLargeEnoughForTotal(context, options, spec) {
    return (
        isLastVisibleDatasetForCategory(context) &&
        fitsCategoryAxis(context, options, 'total', spec)
    );
}

function withStyle(config, options) {
    return {
        ...config,
        ...(options.color !== undefined ? { color: options.color } : {}),
        ...(options.font !== undefined ? { font: options.font } : {}),
    };
}

function buildSegmentLabel(options, spec) {
    const placement = options.placement ?? 'center';
    const isEnd = placement === 'end';

    const config = {
        display: (context) => isLargeEnoughForSegment(context, options, spec),
        formatter: (value, context) =>
            resolveLabelText(context, options, 'segment', spec),
        anchor: () => 'end',
        align: isEnd
            ? (context) =>
                  context.chart.options?.indexAxis === 'y' ? 'right' : 'end'
            : () => 'center',
    };

    if (!isEnd) {
        config.anchor = () => 'center';
    }

    if (options.color !== undefined) {
        config.color = options.color;
    } else if (isEnd) {
        config.color = '#333333';
    } else {
        config.color = (context) =>
            getContrastColor(context.dataset.backgroundColor);
    }

    if (options.font !== undefined) {
        config.font = options.font;
    }

    return config;
}

function buildTotalLabel(options, spec) {
    const placement = options.placement ?? 'outside';
    const align = placement === 'inside' ? () => 'start' : () => 'end';

    return withStyle(
        {
            display: (context) => isLargeEnoughForTotal(context, options, spec),
            formatter: (value, context) =>
                resolveLabelText(context, options, 'total', spec),
            anchor: () => 'end',
            align,
            offset: 4,
        },
        options
    );
}

export default function dataLabels(spec) {
    const labels = spec.annotations?.labels;

    if (!labels?.segment?.display && !labels?.total?.display) {
        return {
            display: false,
        };
    }

    const config = { labels: {} };

    if (labels.segment?.display) {
        config.labels.segment = buildSegmentLabel(labels.segment, spec);
    }

    if (labels.total?.display) {
        config.labels.total = buildTotalLabel(labels.total, spec);
    }

    return config;
}
